import getDesignDataForExport from 'editor/src/store/design/selector/getDesignDataForExport';
import getValueFormatMismatches from 'editor/src/store/design/selector/getValueFormatMismatches';
import selectElementOperation from 'editor/src/store/editor/operation/selectElementOperation';
import { setShowValueFormatErrorsAction } from 'editor/src/store/editor/slice';
import calculateDesignDataWarnings from 'editor/src/store/editorModules/warnings/utils/calculateDesignDataWarnings';
import { extendedWarningsToExportedWarnings } from 'editor/src/store/editorModules/warnings/utils/warningMapper';
import { ImageState } from 'editor/src/store/gallery/types';
import type { ThunkDispatch } from 'editor/src/store/hooks';
import { RootState } from 'editor/src/store/index';
import hasPersonalizationPluginEnabled from 'editor/src/store/plugins/selector/hasPersonalizationPluginEnabled';

import {
  getPersonalizationPricingSession,
  refreshPersonalizationPricing,
} from 'editor/src/util/personalizationPricing/runtime';
import sendPostMessage from 'editor/src/util/postMessages/sendPostMessage';

import type { DesignSaveRequest } from 'editor/src/util/personalizationPricing/types';

/**
 * @param isUserInitiated true when a person asked to save or continue (Continue,
 *   the warnings-popup confirm, a host save intent). False for a host polling
 *   for design data in the background, e.g. the seller wizard re-requesting it
 *   150 ms after every edit — that must never move the selection.
 */
const sendDesignDataPostMessage =
  (checkConditionsAndFillMissing = true, isUserInitiated = false, saveRequest?: DesignSaveRequest) =>
  (dispatch: ThunkDispatch, getState: () => RootState) => {
    const state = getState();
    refreshPersonalizationPricing({ getState });
    const personalizationPricingSession = getPersonalizationPricingSession({
      getState,
    });
    if (saveRequest && saveRequest.sequence !== personalizationPricingSession.getState().sequence) {
      sendPostMessage('design.setDesignData', {
        ...saveRequest,
        error: 'stale_configuration',
      });
      return false;
    }
    if (!personalizationPricingSession.canSubmit()) {
      sendPostMessage('personalization.configurationChanged', personalizationPricingSession.getState());
      if (saveRequest) {
        sendPostMessage('design.setDesignData', {
          ...saveRequest,
          error: 'invalid_artwork',
        });
      }
      return false;
    }
    const { images } = state.gallery;
    const addons = state.editorModules.addons.inUse;

    if (checkConditionsAndFillMissing) {
      if (images.find((image) => image.state !== ImageState.UPLOADED)) {
        if (saveRequest) {
          sendPostMessage('design.setDesignData', {
            ...saveRequest,
            error: 'invalid_artwork',
          });
          return false;
        }
        // eslint-disable-next-line no-alert
        alert('Image upload in progress, please wait and try again later');
        return false;
      }

      // The single gate for a regex forced-format mismatch reaching the host.
      // `checkConditionsAndFillMissing` is true on every path that actually hands
      // the design data onward: Continue (useEcommerceHeader -> requestDesignDataOperation),
      // the warnings-popup confirm, and host-requested design data (design.requestDesignData,
      // including the MIS store / embeddable editor's host.saveDesignIntent -> requestDesignData).
      // changeFormatOperation calls this with `false` on purpose — a product change is not
      // a save, so a mismatch from the previous product must not block it.
      //
      // Not applied while a template is being authored (the personalization
      // plugin is what gives the author the Personalize builder): the regex
      // governs what the BUYER types, and the author's own example text is not
      // held to it — otherwise an author whose example does not match could
      // never save the template, and the seller wizard, which re-requests the
      // design after every edit, would stop receiving it at all.
      //
      // Mismatches first, so the plugin lookup only runs for a design that
      // actually has one; `plugins` is guarded because some callers hand this a
      // partial state.
      const mismatches = getValueFormatMismatches(state.design.designData);
      const isAuthoring = !!state.plugins?.list && hasPersonalizationPluginEnabled(state);
      if (mismatches.length && !isAuthoring) {
        if (saveRequest) {
          sendPostMessage('design.setDesignData', {
            ...saveRequest,
            error: 'invalid_artwork',
          });
        }
        // Only a person asking to save gets taken to the offending layer. A
        // background request is refused just the same (the wrong text must
        // not reach the host), but moving the selection on it would yank the
        // buyer off whatever they are editing after every change.
        if (isUserInitiated) {
          dispatch(selectElementOperation(mismatches[0].uuid));
          // The Personalize tab's own field only shows a mismatch after it has
          // been blurred (see PersonalizationText). A blocked save is the other
          // trigger the spec calls for: it has to surface every mismatch even on
          // a field the buyer never touched, not just the one just selected.
          dispatch(setShowValueFormatErrorsAction(true));
        }
        return false;
      }
    }

    const extendedWarnings = state.design.designData ? calculateDesignDataWarnings(state.design.designData, state) : [];

    const designData = state.design.designData
      ? getDesignDataForExport(
          state.design.designData,
          images,
          addons,
          extendedWarningsToExportedWarnings(extendedWarnings),
          state.hostSettings?.cmykOnly,
        )
      : undefined;
    if (saveRequest) {
      sendPostMessage(
        'design.setDesignData',
        designData ? { ...saveRequest, structure: designData } : { ...saveRequest, error: 'invalid_artwork' },
      );
      return Boolean(designData);
    }
    sendPostMessage('design.setDesignData', designData);

    return true;
  };

export default sendDesignDataPostMessage;
